import React from 'react';
import { Typography, Row, Col, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

interface FooterLinkProps {
  href?: string;
  to?: string;
  children: React.ReactNode;
  external?: boolean;
}

const FooterLink: React.FC<FooterLinkProps> = ({ href, to, children, external }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const langPrefix = i18n.language !== 'en' ? `/${i18n.language}` : '';
  const style: React.CSSProperties = {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 14,
    cursor: 'pointer',
    transition: 'color 0.15s',
    textDecoration: 'none',
    display: 'block',
  };

  if (href) {
    return (
      <a
        href={href}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        style={style}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.9)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.5)'; }}
      >
        {children}
      </a>
    );
  }

  return (
    <span
      role="link"
      tabIndex={0}
      style={style}
      onClick={() => to && navigate(langPrefix + to)}
      onKeyDown={(e) => e.key === 'Enter' && to && navigate(langPrefix + to)}
      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.9)'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.5)'; }}
    >
      {children}
    </span>
  );
};

const ColTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text
    strong
    style={{
      color: 'rgba(255,255,255,0.9)',
      fontSize: 12,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      display: 'block',
      marginBottom: 16,
    }}
  >
    {children}
  </Text>
);

export const AppFooter: React.FC = () => {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  return (
    <footer
      style={{
        background: '#0a1628',
        color: 'rgba(255,255,255,0.6)',
        padding: 'clamp(40px, 6vw, 64px) clamp(16px, 4vw, 48px) 0',
      }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <Row gutter={[48, 32]}>
          {/* Brand column */}
          <Col xs={24} sm={24} md={8}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <img src="/favicon.svg" alt="" aria-hidden="true" style={{ width: 28, height: 28 }} />
              <Text strong style={{ color: '#fff', fontSize: 18, fontWeight: 800 }}>
                Take That Hill!
              </Text>
            </div>
            <Text
              style={{
                color: 'rgba(255,255,255,0.45)',
                fontSize: 13,
                lineHeight: 1.75,
                display: 'block',
              }}
            >
              {t('footer.tagline')}
            </Text>
          </Col>

          {/* Game links */}
          <Col xs={12} sm={8} md={4}>
            <ColTitle>{t('footer.playCol')}</ColTitle>
            <Space direction="vertical" size={10}>
              <FooterLink to="/play/ai">{t('footer.playVsAi')}</FooterLink>
              <FooterLink to="/lobby">{t('footer.multiplayerLobby')}</FooterLink>
              <FooterLink to="/scenarios">{t('footer.scenarios')}</FooterLink>
              <FooterLink to="/rules">{t('footer.gameRules')}</FooterLink>
              <FooterLink to="/leaderboard">{t('footer.leaderboard')}</FooterLink>
              <FooterLink to="/tournament">{t('footer.tournaments')}</FooterLink>
            </Space>
          </Col>

          {/* Blog links */}
          <Col xs={12} sm={8} md={4}>
            <ColTitle>{t('footer.blogCol')}</ColTitle>
            <Space direction="vertical" size={10}>
              <FooterLink to="/blog/mechanics">{t('footer.gameMechanics')}</FooterLink>
              <FooterLink to="/blog/scenarios">{t('footer.scenarios')}</FooterLink>
              <FooterLink to="/blog/strategy">{t('footer.strategy')}</FooterLink>
              <FooterLink to="/blog/advanced-rules">{t('footer.advancedRules')}</FooterLink>
              <FooterLink to="/blog/about-wargame">{t('footer.aboutWargame')}</FooterLink>
              <FooterLink href="/blog/feed.xml" external>{t('footer.rssFeed')}</FooterLink>
            </Space>
          </Col>

          {/* Audience links */}
          <Col xs={12} sm={8} md={4}>
            <ColTitle>{t('footer.forYouCol')}</ColTitle>
            <Space direction="vertical" size={10}>
              <FooterLink to="/for-researchers">{t('footer.forResearchers')}</FooterLink>
              <FooterLink to="/for-military">{t('footer.forMilitary')}</FooterLink>
              <FooterLink to="/for-educators">{t('footer.forEducators')}</FooterLink>
              <FooterLink to="/press">{t('footer.pressKit')}</FooterLink>
            </Space>
          </Col>

          {/* Project links */}
          <Col xs={12} sm={8} md={4}>
            <ColTitle>{t('footer.projectCol')}</ColTitle>
            <Space direction="vertical" size={10}>
              <FooterLink to="/about">{t('footer.about')}</FooterLink>
              <FooterLink to="/changelog">{t('footer.changelog')}</FooterLink>
              <FooterLink to="/learn">{t('footer.learn')}</FooterLink>
              <FooterLink to="/glossary">{t('footer.glossary')}</FooterLink>
              <FooterLink to="/stats">{t('footer.stats')}</FooterLink>
              <FooterLink to="/api-docs">{t('footer.apiDocs')}</FooterLink>
              <FooterLink to="/faq">{t('footer.faq')}</FooterLink>
              <FooterLink to="/profile">{t('footer.myProfile')}</FooterLink>
              <FooterLink to="/privacy">{t('footer.privacy')}</FooterLink>
            </Space>
          </Col>

          {/* Attribution */}
          <Col xs={24} sm={8} md={5}>
            <ColTitle>{t('footer.attributionCol')}</ColTitle>
            <Space direction="vertical" size={10}>
              <FooterLink href="https://www.kcl.ac.uk/people/professor-philip-sabin" external>
                {t('footer.attributionSabin')}
              </FooterLink>
              <FooterLink href="https://www.fightclubinternational.org" external>
                {t('footer.attributionFightClub')}
              </FooterLink>
            </Space>
          </Col>
        </Row>

        {/* Bottom bar */}
        <div
          style={{
            borderTop: '1px solid rgba(255,255,255,0.08)',
            marginTop: 40,
            padding: '20px 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <Text style={{ color: 'rgba(255,255,255,0.28)', fontSize: 12 }}>
            {t('footer.copyright', { year })}
          </Text>
          <Text style={{ color: 'rgba(255,255,255,0.28)', fontSize: 12 }}>
            {t('footer.freeToPlay')}
          </Text>
        </div>
      </div>
    </footer>
  );
};

export default AppFooter;
